import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Download, Upload } from 'lucide-react'
import { FormDialog } from '@/components/common/FormDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toApiProblem } from '@/lib/api/client'
import { useBulkImport, type ImportReport } from '@/lib/bulk'

export interface ImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** What is being imported, in the words the button used — "employees", "grades". */
  what: string
  /** The API prefix the four bulk endpoints hang off, e.g. `/v1/org/grades`. */
  prefix: string
  /** Query key to refresh once rows are actually written. */
  invalidateKey: readonly unknown[]
  /**
   * Offer "create missing departments, designations and grades". Off unless ticked, and only
   * the employee import has it: everywhere else an unknown name is a typo to fix.
   */
  createMissingOption?: boolean
}

/**
 * Importing a spreadsheet, for any entity that offers the bulk endpoints.
 *
 * <p>The dialog enforces the order the server does: <b>check, then import</b>. There is no way
 * to skip the check, because the alternative — upload and hope — is how a hundred and eighty
 * rows arrive wrong and somebody spends a week working out which.
 *
 * <p>The report names the row number the spreadsheet itself shows and the column by its
 * heading, so a problem can be fixed by looking at the file rather than by working out what the
 * API calls that field.
 */
export function ImportDialog({
  open, onOpenChange, what, prefix, invalidateKey, createMissingOption,
}: ImportDialogProps) {
  const [createMissing, setCreateMissing] = useState(false)
  const bulk = useBulkImport(prefix, invalidateKey,
    createMissingOption && createMissing ? { createMissing: true } : undefined)
  const [file, setFile] = useState<File | null>(null)
  const [report, setReport] = useState<ImportReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  const checked = report !== null && !report.applied
  const clean = checked && report.problems.length === 0
  const done = report?.applied === true

  function reset() {
    setFile(null)
    setReport(null)
    setError(null)
    setCreateMissing(false)
  }

  async function run(step: 'check' | 'import') {
    if (!file) return
    setError(null)
    try {
      setReport(step === 'check'
        ? await bulk.validate.mutateAsync(file)
        : await bulk.apply.mutateAsync(file))
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That file could not be read.')
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => { if (!next) reset(); onOpenChange(next) }}
      title={`Import ${what}`}
      description="Fill in the template, check it, then import. Nothing is written until the check comes back clean."
      size="lg"
      submitLabel={done ? 'Close' : clean ? 'Import them' : 'Check the file'}
      loading={bulk.validate.isPending || bulk.apply.isPending}
      error={error}
      onSubmit={() => {
        if (done) {
          reset()
          onOpenChange(false)
        } else {
          void run(clean ? 'import' : 'check')
        }
      }}
    >
      <div className="space-y-4">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => { void bulk.downloadTemplate(`${what}-import-template.xlsx`) }}
        >
          <Download className="size-4" aria-hidden="true" />
          Download the template
        </Button>

        <div className="space-y-1">
          <label htmlFor="import-file" className="text-sm font-medium">Spreadsheet</label>
          <Input
            id="import-file"
            type="file"
            accept=".xlsx,.csv"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null)
              // A new file makes the previous verdict meaningless, and a green "nothing wrong"
              // sitting beside a different file is how the wrong one gets imported.
              setReport(null)
              setError(null)
            }}
          />
          <p className="text-xs text-[var(--color-muted)]">
            .xlsx or .csv. The template's second sheet says what each column expects.
          </p>
        </div>

        {createMissingOption && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-4"
              checked={createMissing}
              onChange={(event) => {
                setCreateMissing(event.target.checked)
                // A different setting can give a different verdict on the same file.
                setReport(null)
              }}
            />
            <span>
              Create missing departments, designations and grades
              <span className="block text-xs text-[var(--color-muted)]">
                Off, a name that matches nothing is reported so a typo can be fixed. On, it is
                added as a new one — check the spelling first, because a typo becomes a second
                department.
              </span>
            </span>
          </label>
        )}

        {report && report.unknownColumns.length > 0 && (
          <p className="flex items-start gap-2 text-sm text-[var(--color-muted)]">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              These columns are not part of the template and were ignored:{' '}
              {report.unknownColumns.join(', ')}. If one of them holds something you meant to
              import, rename it to match the template.
            </span>
          </p>
        )}

        {done && (
          <p className="flex items-start gap-2 text-sm text-[var(--color-success)]">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            {report.accepted} of {report.rowsRead} imported.
          </p>
        )}

        {report && (report.outcomes?.length ?? 0) > 0 && (report.applied || clean) && (
          // An import that can add or update says which it did — "200 imported" does not tell
          // somebody whether the fifty corrections went in as corrections or as fifty new people.
          <div className="max-h-48 overflow-auto rounded-md border border-[var(--color-border)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface)]">
                <tr>
                  <th className="p-2 text-start font-medium">Row</th>
                  <th className="p-2 text-start font-medium">
                    {report.applied ? 'What happened' : 'What will happen'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.outcomes?.map((outcome) => (
                  <tr key={outcome.row} className="border-t border-[var(--color-border)]">
                    <td className="p-2 tabular-nums">{outcome.row}</td>
                    <td className="p-2 text-[var(--color-muted)]">{outcome.outcome}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {clean && (
          <p className="flex items-start gap-2 text-sm text-[var(--color-success)]">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            {report.rowsRead} rows read, nothing wrong with any of them. Nothing has been
            written yet — "Import them" does that.
          </p>
        )}

        {checked && report.problems.length > 0 && (
          <div className="space-y-2">
            <p className="flex items-start gap-2 text-sm text-[var(--color-danger)]">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              {report.rowsRead} rows read, {report.problems.length} problems. Nothing was
              imported — fix the file and check it again. An import is all of it or none of it,
              so a half-corrected file will not leave you reconciling which half went in.
            </p>
            <div className="max-h-64 overflow-auto rounded-md border border-[var(--color-border)]">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-surface)]">
                  <tr>
                    <th className="p-2 text-start font-medium">Row</th>
                    <th className="p-2 text-start font-medium">Column</th>
                    <th className="p-2 text-start font-medium">What is wrong</th>
                  </tr>
                </thead>
                <tbody>
                  {report.problems.map((problem, index) => (
                    <tr
                      key={`${problem.row}-${problem.column ?? ''}-${index}`}
                      className="border-t border-[var(--color-border)]"
                    >
                      <td className="p-2 tabular-nums">
                        {problem.row === 0 ? 'File' : problem.row}
                      </td>
                      <td className="p-2">{problem.column ?? '—'}</td>
                      <td className="p-2 text-[var(--color-muted)]">{problem.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!report && (
          <p className="flex items-start gap-2 text-sm text-[var(--color-muted)]">
            <Upload className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Checking reads the file and reports every row that is wrong. It writes nothing, so
            it is safe to run as often as you like.
          </p>
        )}
      </div>
    </FormDialog>
  )
}
